import { useAuth0 } from "@auth0/auth0-react";
import {
  Activity,
  Barcode,
  PackageSearch,
  LogOut,
  Megaphone,
  Scale,
  Search,
  ShoppingBasket,
  Store,
  Table,
  Tags,
  Ticket,
  TrendingUp,
  Users,
} from "lucide-react";
import { NavLink, Outlet } from "react-router-dom";
import { AUTH_DISABLED, DEV_USER_LABEL } from "@/auth/devMode";
import { GftLogo } from "@/components/GftLogo";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Sales dashboards get their own section here when they land.
const NAV_SECTIONS = [
  {
    title: "Operations",
    items: [
      // `end` stops "/ops" from prefix-matching its sibling routes
      { to: "/ops", label: "Platform statistics", icon: Activity, end: true },
      { to: "/ops/campaigns", label: "Campaign statistics", icon: Megaphone, end: false },
      { to: "/ops/user-lookup", label: "User lookup", icon: Search, end: false },
    ],
  },
  {
    title: "Retailer insights",
    items: [
      { to: "/retailer/activity", label: "Store activity", icon: Store, end: false },
      { to: "/retailer", label: "Redemptions", icon: Ticket, end: true },
      { to: "/retailer/shoppers", label: "Shopper growth", icon: Users, end: false },
    ],
  },
  {
    title: "Product insights",
    items: [
      { to: "/products/campaigns", label: "Campaign impact", icon: TrendingUp, end: false },
      { to: "/products/brand", label: "Brand metrics", icon: Tags, end: false },
      { to: "/products/upc", label: "UPC metrics", icon: Barcode, end: false },
      { to: "/products/compare", label: "UPC comparison", icon: Scale, end: false },
      { to: "/products/demand", label: "Product demand", icon: ShoppingBasket, end: false },
      { to: "/products/raw", label: "Raw sales data", icon: Table, end: false },
    ],
  },
  {
    title: "Product catalog",
    items: [
      { to: "/products/catalog", label: "Search", icon: PackageSearch, end: false },
    ],
  },
];

export function AppLayout() {
  const { user, logout } = useAuth0();

  return (
    <div className="flex min-h-screen">
      <aside
        className="hidden w-60 shrink-0 flex-col md:flex"
        style={{ background: "var(--brand-nav)", color: "var(--brand-nav-foreground)" }}
      >
        <div className="flex h-20 items-center gap-2.5 px-5">
          <GftLogo variant="white" className="h-14 w-auto" />
          <span className="text-sm font-medium tracking-wide">Internal</span>
        </div>
        <nav className="flex flex-1 flex-col gap-6 p-4">
          {NAV_SECTIONS.map((section) => (
            <div key={section.title}>
              <div
                className="mb-2 px-3 text-xs font-medium uppercase tracking-wider"
                style={{ color: "var(--brand-nav-muted)" }}
              >
                {section.title}
              </div>
              <div className="flex flex-col gap-1">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        "brand-nav-link flex items-center gap-2.5 rounded-full px-3 py-2 text-sm transition-colors",
                        isActive && "brand-nav-link-active",
                      )
                    }
                  >
                    <item.icon className="h-4 w-4" strokeWidth={1.5} aria-hidden />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center justify-between gap-4 border-b bg-card px-4 md:px-6">
          {/* Small screens: the sidebar is hidden, so the nav collapses to a
              horizontally scrollable strip — 12+ links can't fit a phone. */}
          <nav className="flex min-w-0 items-center gap-2 overflow-x-auto md:hidden">
            <GftLogo className="mr-1 h-8 w-auto shrink-0" />
            {NAV_SECTIONS.flatMap((s) => s.items).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    "shrink-0 whitespace-nowrap rounded-md px-2 py-1 text-sm",
                    isActive
                      ? "bg-accent font-medium"
                      : "text-muted-foreground",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden text-sm text-muted-foreground sm:inline">
              {AUTH_DISABLED ? DEV_USER_LABEL : user?.email}
            </span>
            {AUTH_DISABLED ? null : (
              <Button
                variant="ghost"
                size="sm"
                aria-label="Sign out"
                onClick={() =>
                  void logout({
                    logoutParams: { returnTo: window.location.origin },
                  })
                }
              >
                <LogOut className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Sign out</span>
              </Button>
            )}
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
